import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import {
  subscribeToThread,
  subscribeToThreadMessages,
  subscribeToPinnedMessages,
  subscribeToScheduledMessages,
  sendThreadMessage,
  scheduleMessage,
  cancelScheduledMessage,
  markThreadRead,
  toggleMessageReaction,
  toggleMessagePin,
  updateThreadChildren,
  messageSeenState,
  THREAD_PAGE_SIZE,
} from '../../services/messageService';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import { validateAttachment } from '@coparent/shared/src/services/attachmentService';
import {
  DOCUMENT_CATEGORIES,
  ALLOWED_FILE_TYPES,
  REACTION_EMOJIS,
  MESSAGE_LINK_TYPES,
  SCHEDULED_SEND_PRESETS,
} from '@coparent/shared/src/utils/constants';
import {
  quietHoursEndsAt,
  isQuietNow,
  formatQuietWindow,
} from '@coparent/shared/src/utils/quietHours';
import { subscribeToDeliveryPrefs } from '@coparent/shared/src/services/userPrefsService';
import { uploadFileAttachment } from '../../utils/attachmentUpload';
import AttachmentView from '../common/AttachmentView';
import {
  HiPaperAirplane,
  HiPaperClip,
  HiEmojiHappy,
  HiOutlineClock,
  HiOutlineBookmark,
  HiBookmark,
  HiX,
  HiOutlineTag,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

// Hover-reveal reaction button with an emoji popover.
const ReactionButton = ({ open, onToggleOpen, onPick }) => (
  <div className="relative">
    <button
      type="button"
      onClick={onToggleOpen}
      className={`w-7 h-7 rounded-full flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-all ${
        open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}
      aria-label="Add reaction"
    >
      <HiEmojiHappy className="text-base" />
    </button>
    {open && (
      <div className="absolute bottom-9 left-1/2 -translate-x-1/2 z-10 flex gap-1 px-2 py-1.5 bg-white rounded-full shadow-warm-lg border border-surface-100">
        {REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onPick(emoji)}
            className="text-xl leading-none hover:scale-125 transition-transform p-0.5"
          >
            {emoji}
          </button>
        ))}
      </div>
    )}
  </div>
);

/** Hover-reveal pin toggle. */
const PinButton = ({ pinned, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
      pinned
        ? 'opacity-100 text-sunbeam'
        : 'opacity-0 group-hover:opacity-100 text-ink-400 hover:bg-surface-100 hover:text-ink-600'
    }`}
    aria-label={pinned ? 'Unpin message' : 'Pin message'}
    title={pinned ? 'Unpin message' : 'Pin message'}
  >
    {pinned ? <HiBookmark className="text-base" /> : <HiOutlineBookmark className="text-base" />}
  </button>
);

const MessageThread = ({ threadId, threadTitle }) => {
  const { currentUser } = useAuth();
  const {
    familyId,
    familyChildren,
    childrenMap,
    getCoParent,
    getParticipantProfile,
  } = useFamily();

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pageLimit, setPageLimit] = useState(THREAD_PAGE_SIZE);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinned, setShowPinned] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [saveToDocs, setSaveToDocs] = useState(false);
  const [docCategory, setDocCategory] = useState('other');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [reactionOpenId, setReactionOpenId] = useState(null);
  const [scheduleMenuOpen, setScheduleMenuOpen] = useState(false);
  const [customSchedule, setCustomSchedule] = useState('');
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [coParentPrefs, setCoParentPrefs] = useState({ quietHours: null, timezone: null });

  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  // Set while a "load earlier" expansion is in flight so the arrival of older
  // messages doesn't yank the viewport back to the bottom.
  const pagingRef = useRef(false);

  const coParent = getCoParent?.() || null;
  // getCoParent returns the family's parentProfiles entry keyed as `id`.
  const coParentId = coParent?.id || null;

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!familyId || !threadId) {
      setThread(null);
      return;
    }
    return subscribeToThread(familyId, threadId, setThread);
  }, [familyId, threadId]);

  // Quiet hours live on the co-parent's own user doc, not on the family's
  // parentProfiles — they are a property of a person, and co-parents in
  // different timezones are common here. See userPrefsService.
  useEffect(() => {
    if (!coParentId) {
      setCoParentPrefs({ quietHours: null, timezone: null });
      return;
    }
    return subscribeToDeliveryPrefs(coParentId, setCoParentPrefs);
  }, [coParentId]);

  // Reset paging whenever the user switches threads, so a deep history in one
  // conversation doesn't make the next one load 500 messages on open.
  useEffect(() => {
    setPageLimit(THREAD_PAGE_SIZE);
    setShowPinned(false);
    pagingRef.current = false;
  }, [threadId]);

  useEffect(() => {
    if (!familyId || !threadId) return;
    const unsub = subscribeToThreadMessages(
      familyId,
      threadId,
      (msgs, meta) => {
        setMessages(msgs);
        setHasMore(!!meta?.hasMore);
      },
      pageLimit
    );
    markThreadRead(familyId, threadId, currentUser.uid);
    return unsub;
  }, [familyId, threadId, currentUser.uid, pageLimit]);

  useEffect(() => {
    if (!familyId || !threadId) return;
    return subscribeToPinnedMessages(familyId, threadId, setPinnedMessages);
  }, [familyId, threadId]);

  useEffect(() => {
    if (!familyId || !threadId) return;
    return subscribeToScheduledMessages(familyId, threadId, setScheduled);
  }, [familyId, threadId]);

  // Mark read when new messages arrive
  useEffect(() => {
    if (messages.length > 0 && familyId && threadId) {
      markThreadRead(familyId, threadId, currentUser.uid);
    }
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pagingRef.current) {
      // Older messages just prepended — hold position instead of scrolling.
      pagingRef.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Derived state ─────────────────────────────────────────────────────────

  // Receipts render on the newest message the current user sent, iMessage-style:
  // a receipt on every bubble is noise, and the only one that answers "did they
  // see it?" is the last.
  const lastOwnMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].senderId === currentUser?.uid) return messages[i].id;
    }
    return null;
  }, [messages, currentUser?.uid]);

  const seenState = useMemo(() => {
    if (!lastOwnMessageId || !thread) return null;
    const message = messages.find((m) => m.id === lastOwnMessageId);
    if (!message) return null;
    return messageSeenState(thread, message, currentUser.uid);
  }, [thread, messages, lastOwnMessageId, currentUser?.uid]);

  const threadTags = useMemo(
    () => (thread?.childIds || []).map((id) => childrenMap?.[id]).filter(Boolean),
    [thread?.childIds, childrenMap]
  );

  // Compose-time quiet-hours awareness: if the co-parent is asleep by their own
  // setting, offer to hold the message rather than silently sending something
  // they won't be notified about.
  const coParentQuiet = useMemo(() => {
    const { quietHours, timezone } = coParentPrefs;
    if (!quietHours || !isQuietNow(quietHours, timezone)) return null;
    return {
      endsAt: quietHoursEndsAt(quietHours, timezone),
      window: formatQuietWindow(quietHours),
      name: coParent?.displayName?.split(' ')[0] || 'Your co-parent',
    };
  }, [coParentPrefs, coParent]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handleToggleReaction = async (msg, emoji, hasReacted) => {
    setReactionOpenId(null);
    try {
      await toggleMessageReaction(familyId, threadId, msg.id, emoji, currentUser.uid, hasReacted);
    } catch (error) {
      toast.error('Could not react to message');
    }
  };

  const handleTogglePin = async (msg) => {
    try {
      await toggleMessagePin(familyId, threadId, msg.id, !msg.pinned, currentUser.uid);
      toast.success(msg.pinned ? 'Unpinned' : 'Pinned to this conversation');
    } catch (error) {
      toast.error('Could not update pin');
    }
  };

  const handleToggleChildTag = async (childId) => {
    const current = thread?.childIds || [];
    const next = current.includes(childId)
      ? current.filter((id) => id !== childId)
      : [...current, childId];
    try {
      await updateThreadChildren(familyId, threadId, next);
    } catch (error) {
      toast.error('Could not update tags');
    }
  };

  const handleLoadEarlier = () => {
    pagingRef.current = true;
    setPageLimit((limit) => limit + THREAD_PAGE_SIZE);
  };

  const clearPending = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview(null);
    setSaveToDocs(false);
    setDocCategory('other');
    setUploadProgress(0);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const check = validateAttachment(file);
    if (!check.valid) {
      toast.error(check.error);
      return;
    }
    setPendingFile(file);
    setPendingPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  };

  /**
   * Upload the pending attachment, if any. Shared by immediate and scheduled
   * sends — a queued message stores the already-uploaded attachment reference,
   * so delivery never depends on the sender's device still being around.
   */
  const uploadPendingIfAny = useCallback(async () => {
    if (!pendingFile) return undefined;
    setUploadProgress(0);
    const stored = await uploadFileAttachment(familyId, pendingFile, {
      scope: 'thread',
      parentId: threadId,
      saveToDocs,
      category: docCategory,
      currentUser,
      onProgress: setUploadProgress,
    });
    return [stored];
  }, [pendingFile, familyId, threadId, saveToDocs, docCategory, currentUser]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingFile) || !familyId || !threadId) return;

    setSending(true);
    try {
      const attachments = await uploadPendingIfAny();
      await sendThreadMessage(familyId, threadId, {
        text: newMessage.trim(),
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Unknown',
        ...(attachments ? { attachments } : {}),
        // Only claim the title when the thread genuinely has none. A linked
        // thread is created with a seeded title ("Soccer cleats · $45.00") and
        // must not have it overwritten by whatever the first message says.
        _isFirstMessage: messages.length === 0 && !(thread?.title || '').trim(),
      });
      setNewMessage('');
      clearPending();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  /** Queue the composed message for a future time. */
  const handleSchedule = async (when) => {
    if ((!newMessage.trim() && !pendingFile) || !familyId || !threadId) return;
    if (!when || Number.isNaN(when.getTime())) {
      toast.error('Pick a valid time');
      return;
    }
    if (when.getTime() <= Date.now()) {
      toast.error('Pick a time in the future');
      return;
    }

    setSending(true);
    setScheduleMenuOpen(false);
    try {
      const attachments = await uploadPendingIfAny();
      await scheduleMessage(familyId, {
        threadId,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Unknown',
        text: newMessage.trim(),
        attachments,
        scheduledFor: when,
      });
      setNewMessage('');
      setCustomSchedule('');
      clearPending();
      toast.success(`Scheduled for ${formatDate(when, 'EEE, MMM d · h:mm a')}`);
    } catch (error) {
      console.error('Failed to schedule message:', error);
      toast.error('Could not schedule message');
    }
    setSending(false);
  };

  /** Resolve a preset from SCHEDULED_SEND_PRESETS against the sender's clock. */
  const resolvePreset = (preset) => {
    const when = new Date();
    if (preset.offsetMinutes) {
      when.setMinutes(when.getMinutes() + preset.offsetMinutes);
      return when;
    }
    when.setDate(when.getDate() + (preset.dayOffset || 0));
    when.setHours(preset.hour, 0, 0, 0);
    // "This evening" after 6pm has already passed — roll it to tomorrow rather
    // than offering a time in the past.
    if (when.getTime() <= Date.now()) when.setDate(when.getDate() + 1);
    return when;
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      await cancelScheduledMessage(familyId, scheduledId);
      toast.success('Scheduled message canceled');
    } catch (error) {
      toast.error('Could not cancel');
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!threadId) {
    return (
      <div className="flex items-center justify-center h-full text-ink-300">
        <div className="text-center">
          <p className="text-base font-semibold">Select a conversation</p>
          <p className="text-sm mt-1">or start a new one</p>
        </div>
      </div>
    );
  }

  const linkMeta = thread?.linkedEntity?.type
    ? MESSAGE_LINK_TYPES[thread.linkedEntity.type]
    : null;
  const canSend = !sending && (newMessage.trim() || pendingFile);
  const displayedMessages = showPinned ? pinnedMessages : messages;

  const renderMessage = (msg) => {
    const isMe = msg.senderId === currentUser.uid;
    const senderProfile = getParticipantProfile(msg.senderId);
    const reactionEntries = Object.entries(msg.reactions || {}).filter(
      ([, uids]) => uids?.length > 0
    );
    const showSeen = !showPinned && msg.id === lastOwnMessageId && seenState?.seen;

    return (
      <div
        key={msg.id}
        className={`group flex items-center gap-1.5 ${isMe ? 'justify-end' : 'justify-start'}`}
      >
        {isMe && (
          <>
            <PinButton pinned={!!msg.pinned} onToggle={() => handleTogglePin(msg)} />
            <ReactionButton
              open={reactionOpenId === msg.id}
              onToggleOpen={() => setReactionOpenId(reactionOpenId === msg.id ? null : msg.id)}
              onPick={(emoji) =>
                handleToggleReaction(msg, emoji, !!msg.reactions?.[emoji]?.includes(currentUser.uid))
              }
            />
          </>
        )}
        <div className={`max-w-[75%] ${isMe ? 'order-1' : ''}`}>
          {msg.attachments?.length > 0 && (
            <div className={`flex flex-col gap-1 mb-1 ${isMe ? 'items-end' : 'items-start'}`}>
              {msg.attachments.map((att, i) => (
                <AttachmentView key={att.storagePath || i} attachment={att} />
              ))}
            </div>
          )}
          {!!msg.text && (
            <div
              className={`px-4 py-2.5 text-sm ${
                isMe
                  ? 'bg-ink-800 text-white rounded-2xl rounded-br-lg'
                  : 'bg-surface-100 text-ink-800 rounded-2xl rounded-bl-lg'
              } ${msg.pinned ? 'ring-2 ring-sunbeam/60' : ''}`}
            >
              <p>{msg.text}</p>
            </div>
          )}
          {reactionEntries.length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
              {reactionEntries.map(([emoji, uids]) => {
                const mine = uids.includes(currentUser.uid);
                return (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleToggleReaction(msg, emoji, mine)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs transition-colors ${
                      mine
                        ? 'bg-accent-50 border-accent-300 text-accent-700'
                        : 'bg-surface-50 border-surface-200 text-ink-500 hover:bg-surface-100'
                    }`}
                  >
                    <span>{emoji}</span>
                    <span className="font-bold">{uids.length}</span>
                  </button>
                );
              })}
            </div>
          )}
          <p className={`text-[0.625rem] text-ink-300 mt-1 font-medium ${isMe ? 'text-right' : ''}`}>
            {!isMe && senderProfile?.displayName && `${senderProfile.displayName} · `}
            {formatDate(msg.createdAt, 'h:mm a')}
            {msg.deliveredFromSchedule && ' · scheduled'}
            {showPinned && ` · ${formatDate(msg.createdAt, 'MMM d')}`}
          </p>
          {showSeen && (
            <p className="text-[0.625rem] text-ink-300 mt-0.5 font-medium text-right">
              Seen{seenState.seenAt ? ` ${formatDate(new Date(seenState.seenAt), 'h:mm a')}` : ''}
            </p>
          )}
        </div>
        {!isMe && (
          <>
            <ReactionButton
              open={reactionOpenId === msg.id}
              onToggleOpen={() => setReactionOpenId(reactionOpenId === msg.id ? null : msg.id)}
              onPick={(emoji) =>
                handleToggleReaction(msg, emoji, !!msg.reactions?.[emoji]?.includes(currentUser.uid))
              }
            />
            <PinButton pinned={!!msg.pinned} onToggle={() => handleTogglePin(msg)} />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-5 py-3 border-b border-surface-100">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-ink-800 truncate">
            {thread?.title || threadTitle || 'New Conversation'}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Child tagging */}
            {familyChildren?.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setTagMenuOpen((open) => !open)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors"
                  title="Tag children"
                >
                  <HiOutlineTag className="text-base" />
                </button>
                {tagMenuOpen && (
                  <div className="absolute right-0 top-9 z-20 w-48 py-1.5 bg-white rounded-xl shadow-warm-lg border border-surface-100">
                    <p className="px-3 py-1 text-[0.625rem] font-bold uppercase tracking-wide text-ink-300">
                      This conversation is about
                    </p>
                    {familyChildren.map((child) => {
                      const active = (thread?.childIds || []).includes(child.id);
                      return (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => handleToggleChildTag(child.id)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-ink-700 hover:bg-surface-50 transition-colors"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: childrenMap?.[child.id]?.color || '#B8B2A8' }}
                          />
                          <span className="flex-1 text-left truncate">{child.name}</span>
                          {active && <span className="text-accent-600 font-bold">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Pinned toggle */}
            {pinnedMessages.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPinned((open) => !open)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
                  showPinned
                    ? 'bg-sunbeam/25 text-ink-700'
                    : 'text-ink-400 hover:bg-surface-100 hover:text-ink-600'
                }`}
                title="Pinned messages"
              >
                <HiBookmark className="text-sm" />
                {pinnedMessages.length}
              </button>
            )}
          </div>
        </div>

        {/* Linked record context */}
        {thread?.linkedEntity && (
          <Link
            to={linkMeta?.webPath || '/dashboard'}
            className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-accent-50 hover:bg-accent-100 transition-colors"
          >
            <span className="text-[0.625rem] font-bold uppercase tracking-wide text-accent-700">
              {linkMeta?.label || 'Linked'}
            </span>
            <span className="text-xs text-accent-700 font-medium truncate max-w-[16rem]">
              {thread.linkedEntity.label}
            </span>
          </Link>
        )}

        {/* Child tags */}
        {threadTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {threadTags.map((child) => (
              <span
                key={child.id}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-surface-100 text-ink-500 text-[0.625rem] font-semibold"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: child.color || '#B8B2A8' }}
                />
                {child.name?.split(' ')[0]}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
        {showPinned && (
          <div className="flex items-center justify-between px-1 pb-1">
            <p className="text-[0.625rem] font-bold uppercase tracking-wide text-ink-300">
              Pinned messages
            </p>
            <button
              type="button"
              onClick={() => setShowPinned(false)}
              className="text-xs text-accent-600 font-semibold hover:underline"
            >
              Back to conversation
            </button>
          </div>
        )}

        {!showPinned && hasMore && (
          <div className="text-center pb-2">
            <button
              type="button"
              onClick={handleLoadEarlier}
              className="px-3 py-1.5 text-xs font-semibold text-accent-600 bg-accent-50 hover:bg-accent-100 rounded-full transition-colors"
            >
              Load earlier messages
            </button>
          </div>
        )}

        {displayedMessages.length === 0 && (
          <div className="text-center text-ink-300 py-16">
            <p className="text-base font-semibold">
              {showPinned ? 'No pinned messages' : 'No messages yet'}
            </p>
            <p className="text-sm mt-1">
              {showPinned
                ? 'Pin school info or medication details to keep them handy.'
                : 'Send the first message to start this conversation.'}
            </p>
          </div>
        )}

        {displayedMessages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>

      {/* Queued messages awaiting delivery */}
      {scheduled.length > 0 && (
        <div className="px-5 py-2 border-t border-surface-100 bg-surface-50/60 space-y-1.5">
          {scheduled.map((item) => (
            <div key={item.id} className="flex items-center gap-2 text-xs">
              <HiOutlineClock className="text-ink-400 flex-shrink-0" />
              <span className="text-ink-500 flex-shrink-0 font-semibold">
                {formatDate(item.scheduledFor, 'EEE h:mm a')}
              </span>
              <span className="text-ink-400 truncate flex-1">{item.text || 'Attachment'}</span>
              <button
                type="button"
                onClick={() => handleCancelScheduled(item.id)}
                className="text-ink-300 hover:text-ink-600 transition-colors flex-shrink-0"
                aria-label="Cancel scheduled message"
              >
                <HiX />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Quiet-hours nudge */}
      {coParentQuiet && (newMessage.trim() || pendingFile) && (
        <div className="px-5 py-2 border-t border-surface-100 bg-sunbeam/10 flex items-center gap-2">
          <HiOutlineClock className="text-ink-500 flex-shrink-0" />
          <p className="text-xs text-ink-600 flex-1">
            {coParentQuiet.name}&apos;s quiet hours are on ({coParentQuiet.window}). They won&apos;t
            be notified until morning.
          </p>
          {coParentQuiet.endsAt && (
            <button
              type="button"
              onClick={() => handleSchedule(coParentQuiet.endsAt)}
              disabled={sending}
              className="text-xs font-bold text-accent-700 hover:underline flex-shrink-0 disabled:opacity-40"
            >
              Send at {formatDate(coParentQuiet.endsAt, 'h:mm a')}
            </button>
          )}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-surface-100 bg-white">
        {pendingFile && (
          <div className="mb-3 flex items-start gap-4">
            <AttachmentView
              attachment={{
                fileName: pendingFile.name,
                fileType: pendingFile.type,
                previewUrl: pendingPreview,
              }}
              uploading={sending}
              progress={sending ? uploadProgress : null}
              onRemove={clearPending}
            />
            <div className="flex-1">
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={saveToDocs}
                  onChange={(e) => setSaveToDocs(e.target.checked)}
                  className="rounded border-surface-300 text-accent-500 focus:ring-accent-400"
                />
                Save to Documents
              </label>
              {saveToDocs && (
                <select
                  value={docCategory}
                  onChange={(e) => setDocCategory(e.target.value)}
                  className="mt-2 px-3 py-1.5 bg-surface-50 border border-surface-200 rounded-lg text-sm text-ink-800 outline-none focus:ring-2 focus:ring-accent-200"
                >
                  {DOCUMENT_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
            className="hidden"
            onChange={(e) => {
              handleFileSelect(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="w-10 h-10 text-ink-400 hover:text-ink-700 rounded-full flex items-center justify-center transition-colors disabled:opacity-40"
            aria-label="Attach file"
          >
            <HiPaperClip className="text-lg" />
          </button>

          <input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            type="text"
            className="flex-1 px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-full text-sm text-ink-800 placeholder:text-ink-300 focus:ring-2 focus:ring-accent-200 focus:border-accent-400 outline-none transition-all duration-200"
            placeholder="Type a message..."
          />

          {/* Send later */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setScheduleMenuOpen((open) => !open)}
              disabled={!canSend}
              className="w-10 h-10 text-ink-400 hover:text-ink-700 rounded-full flex items-center justify-center transition-colors disabled:opacity-40"
              aria-label="Send later"
              title="Send later"
            >
              <HiOutlineClock className="text-lg" />
            </button>
            {scheduleMenuOpen && canSend && (
              <div className="absolute bottom-12 right-0 z-20 w-56 py-1.5 bg-white rounded-xl shadow-warm-lg border border-surface-100">
                <p className="px-3 py-1 text-[0.625rem] font-bold uppercase tracking-wide text-ink-300">
                  Send later
                </p>
                {SCHEDULED_SEND_PRESETS.map((preset) => {
                  const when = resolvePreset(preset);
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => handleSchedule(when)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-ink-700 hover:bg-surface-50 transition-colors"
                    >
                      <span>{preset.label}</span>
                      <span className="text-[0.625rem] text-ink-300 font-medium">
                        {formatDate(when, 'h:mm a')}
                      </span>
                    </button>
                  );
                })}
                <div className="px-3 pt-2 pb-1 border-t border-surface-100 mt-1">
                  <label className="text-[0.625rem] font-bold uppercase tracking-wide text-ink-300">
                    Custom
                  </label>
                  <input
                    type="datetime-local"
                    value={customSchedule}
                    onChange={(e) => setCustomSchedule(e.target.value)}
                    className="w-full mt-1 px-2 py-1 bg-surface-50 border border-surface-200 rounded-lg text-xs text-ink-800 outline-none focus:ring-2 focus:ring-accent-200"
                  />
                  <button
                    type="button"
                    disabled={!customSchedule}
                    onClick={() => handleSchedule(new Date(customSchedule))}
                    className="w-full mt-1.5 px-2 py-1 bg-ink-800 text-white rounded-lg text-xs font-semibold disabled:opacity-40 hover:bg-ink-900 transition-colors"
                  >
                    Schedule
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSend}
            className="w-10 h-10 bg-ink-800 text-white rounded-full hover:bg-ink-900 transition-all duration-200 disabled:opacity-40 flex items-center justify-center active:scale-95"
          >
            <HiPaperAirplane className="text-base rotate-90" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageThread;
